import { useState, useCallback } from 'react';

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  email?: boolean;
  phone?: boolean;
  custom?: (value: string) => string | null;
  message?: string;
}

export interface ValidationRules {
  [key: string]: ValidationRule;
}

export interface ValidationErrors {
  [key: string]: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationErrors;
}

export const useFormValidation = (rules: ValidationRules) => {
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const validateField = useCallback(
    (name: string, value: string): string | null => {
      const rule = rules[name];
      if (!rule) return null;

      // Required validation
      if (rule.required && !value.trim()) {
        return rule.message || '此欄位為必填';
      }

      // Skip other validations if empty and not required
      if (!value.trim()) return null;

      // Min length validation
      if (rule.minLength && value.length < rule.minLength) {
        return rule.message || `至少需要 ${rule.minLength} 個字元`;
      }

      // Max length validation
      if (rule.maxLength && value.length > rule.maxLength) {
        return rule.message || `最多 ${rule.maxLength} 個字元`;
      }

      // Email validation
      if (rule.email) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(value)) {
          return rule.message || '請輸入有效的電子郵件地址';
        }
      }

      // Phone validation (Taiwan format)
      if (rule.phone) {
        const phonePattern = /^(\+886|0)?[2-9]\d{7,9}$/;
        if (!phonePattern.test(value.replace(/[-\s]/g, ''))) {
          return rule.message || '請輸入有效的電話號碼';
        }
      }

      // Pattern validation
      if (rule.pattern && !rule.pattern.test(value)) {
        return rule.message || '格式不正確';
      }

      // Custom validation
      if (rule.custom) {
        return rule.custom(value);
      }

      return null;
    },
    [rules]
  );

  const validateForm = useCallback(
    (formData: Record<string, string>): ValidationResult => {
      const newErrors: ValidationErrors = {};
      let isValid = true;

      Object.keys(rules).forEach((fieldName) => {
        const error = validateField(fieldName, formData[fieldName] || '');
        if (error) {
          newErrors[fieldName] = error;
          isValid = false;
        }
      });

      setErrors(newErrors);
      if (!isValid) {
        setTouched((prev) => {
          const next = new Set(prev);
          Object.keys(newErrors).forEach((fieldName) => next.add(fieldName));
          return next;
        });
      }
      return { isValid, errors: newErrors };
    },
    [rules, validateField]
  );

  const handleBlur = useCallback((fieldName: string, value: string) => {
    setTouched((prev) => new Set(prev).add(fieldName));
    const error = validateField(fieldName, value);
    setErrors((prev) => ({
      ...prev,
      [fieldName]: error || '',
    }));
  }, [validateField]);

  const handleChange = useCallback((fieldName: string, value: string) => {
    // Only validate if field has been touched
    if (touched.has(fieldName)) {
      const error = validateField(fieldName, value);
      setErrors((prev) => ({
        ...prev,
        [fieldName]: error || '',
      }));
    }
  }, [touched, validateField]);

  const clearError = useCallback((fieldName: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
    setTouched(new Set());
  }, []);

  const hasError = useCallback((fieldName: string): boolean => {
    return touched.has(fieldName) && !!errors[fieldName];
  }, [touched, errors]);

  const getError = useCallback((fieldName: string): string | undefined => {
    return touched.has(fieldName) ? errors[fieldName] : undefined;
  }, [touched, errors]);

  return {
    errors,
    touched,
    validateForm,
    validateField,
    handleBlur,
    handleChange,
    clearError,
    clearAllErrors,
    hasError,
    getError,
  };
};
